/**
 * Q AI Eval Pipeline API Service
 * Handles all eval pipeline operations for admin panel
 *
 * Server: FIX Engine (baseURL)
 * Auth: JWT Bearer token with Admin/MasterAdmin role
 *
 * Endpoints:
 * - GET    /api/admin/qai/eval/dashboard          - Get dashboard metrics
 * - GET    /api/admin/qai/eval/cases              - List all eval cases
 * - GET    /api/admin/qai/eval/cases/{id}         - Get single case
 * - POST   /api/admin/qai/eval/cases              - Create new case
 * - PUT    /api/admin/qai/eval/cases/{id}         - Update case
 * - DELETE /api/admin/qai/eval/cases/{id}         - Delete case
 * - GET    /api/admin/qai/eval/runs               - List all runs
 * - GET    /api/admin/qai/eval/runs/{id}          - Get run details with results
 * - POST   /api/admin/qai/eval/runs               - Start new eval run
 * - GET    /api/admin/qai/eval/compare            - Compare two versions
 * - GET    /api/admin/qai/eval/export/{runId}     - Export run results as JSON
 */

import axios from 'axios';
import { getQAIAdminConfig } from '../config';

// Get routing configuration (DataFeed direct or FIX Engine proxy)
const { baseUrl, pathPrefix } = getQAIAdminConfig();

// ═══════════════════════════════════════════════════════════════════════════
// HELPER - Get auth headers
// ═══════════════════════════════════════════════════════════════════════════

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get eval dashboard metrics
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getEvalDashboard = async () => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/eval/dashboard`, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to fetch eval dashboard:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch dashboard'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// EVAL CASES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get all eval cases
 * @param {object} params - Query parameters
 * @param {string} [params.category] - Filter by category
 * @param {boolean} [params.isActive] - Filter by active status
 * @returns {Promise<{success: boolean, data: Array, error?: string}>}
 */
export const getEvalCases = async (params = {}) => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/eval/cases`, {
            headers: getAuthHeaders(),
            params
        });
        return {
            success: true,
            data: response.data.data || response.data || []
        };
    } catch (error) {
        console.error('Failed to fetch eval cases:', error);
        return {
            success: false,
            data: [],
            error: error.response?.data?.message || error.message || 'Failed to fetch cases'
        };
    }
};

/**
 * Get single eval case by ID
 * @param {number} caseId - The case ID
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getEvalCaseById = async (caseId) => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/eval/cases/${caseId}`, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to fetch eval case:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch case'
        };
    }
};

/**
 * Create new eval case
 * @param {object} caseData - The case data
 * @param {string} caseData.category - Case category
 * @param {string} caseData.question - The test question
 * @param {string} caseData.expectedBehavior - Expected response behavior
 * @param {number} caseData.difficulty - Difficulty level (1-5)
 * @param {string} [caseData.context] - Optional context
 * @param {boolean} [caseData.isActive] - Active status
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const createEvalCase = async (caseData) => {
    try {
        const response = await axios.post(`${baseUrl}${pathPrefix}/eval/cases`, caseData, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to create eval case:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to create case'
        };
    }
};

/**
 * Update eval case
 * @param {number} caseId - The case ID
 * @param {object} caseData - The updated case data
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const updateEvalCase = async (caseId, caseData) => {
    try {
        const response = await axios.put(`${baseUrl}${pathPrefix}/eval/cases/${caseId}`, caseData, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to update eval case:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to update case'
        };
    }
};

/**
 * Delete eval case (soft delete)
 * @param {number} caseId - The case ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteEvalCase = async (caseId) => {
    try {
        await axios.delete(`${baseUrl}${pathPrefix}/eval/cases/${caseId}`, {
            headers: getAuthHeaders()
        });
        return { success: true };
    } catch (error) {
        console.error('Failed to delete eval case:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to delete case'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// EVAL RUNS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get all eval runs
 * @param {object} params - Query parameters
 * @param {string} [params.promptVersion] - Filter by prompt version
 * @param {number} [params.limit] - Limit results
 * @returns {Promise<{success: boolean, data: Array, error?: string}>}
 */
export const getEvalRuns = async (params = {}) => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/eval/runs`, {
            headers: getAuthHeaders(),
            params
        });
        return {
            success: true,
            data: response.data.data || response.data || []
        };
    } catch (error) {
        console.error('Failed to fetch eval runs:', error);
        return {
            success: false,
            data: [],
            error: error.response?.data?.message || error.message || 'Failed to fetch runs'
        };
    }
};

/**
 * Get eval run details with results
 * @param {number} runId - The run ID
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getEvalRunById = async (runId) => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/eval/runs/${runId}`, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to fetch eval run:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch run'
        };
    }
};

/**
 * Start new eval run
 * @param {object} runData - The run parameters
 * @param {string} runData.promptVersion - Version identifier for this run
 * @param {string} [runData.notes] - Optional notes about this run
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const startEvalRun = async (runData) => {
    try {
        // Note: POST endpoint is /eval/run (singular), not /eval/runs (plural)
        const response = await axios.post(`${baseUrl}${pathPrefix}/eval/run`, runData, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to start eval run:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to start run'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPARISON & EXPORT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compare two eval run versions
 * @param {string} version1 - First version to compare
 * @param {string} version2 - Second version to compare
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const compareVersions = async (version1, version2) => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/eval/compare`, {
            headers: getAuthHeaders(),
            params: { version1, version2 }
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to compare versions:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to compare versions'
        };
    }
};

/**
 * Export eval run results as JSON
 * @param {string} version - The version name to export (e.g., "V1")
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const exportEvalRun = async (version) => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/eval/export`, {
            headers: getAuthHeaders(),
            params: { version }
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to export eval run:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to export run'
        };
    }
};

/**
 * Download export as file
 * @param {string} version - The version name to export (e.g., "V1")
 * @param {string} filename - Desired filename
 */
export const downloadEvalExport = async (version, filename = 'eval-results.json') => {
    try {
        const result = await exportEvalRun(version);
        if (result.success) {
            const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            return { success: true };
        }
        return result;
    } catch (error) {
        console.error('Failed to download export:', error);
        return {
            success: false,
            error: error.message || 'Failed to download export'
        };
    }
};
